import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api.js';
import { fmtRelativeTime } from '../utils/format.js';
import PaymentBadge from '../components/PaymentBadge.jsx';
import {
  STATUS_LABEL, statusTone, TONE_CLASSES,
  REASON_OPTIONS, REASON_LABEL, ACTIONABLE_STATUSES,
} from '../utils/statusMeta.js';

/**
 * Unified inbox — claims + found reports in one feed, with identification
 * actions (stage 2b): confirm-with-picker + on-the-spot creation for
 * reports, reject-with-reason, and confirm/reject for claims.
 * Item physical-recovery (IN_RECOVERY → RECOVERED) lives on the item page.
 */

export default function Inbox() {
  const [feed, setFeed] = useState(null);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState('all');

  const reload = () =>
    api.listInbox().then((r) => setFeed(r.inbox)).catch((e) => setErr(e.message));
  useEffect(() => { reload(); }, []);

  if (err) return <div className="alert error">{err}</div>;
  if (!feed) return <div className="muted center loading-block">Loading…</div>;

  const shown = feed.filter((entry) => {
    if (filter === 'all') return true;
    const tone = statusTone(entry.status);
    if (filter === 'new') return tone === 'new' || tone === 'viewed';
    if (filter === 'active') return tone === 'active';
    if (filter === 'closed') return tone === 'closed' || tone === 'done';
    return true;
  });
  const newCount = feed.filter((e) => statusTone(e.status) === 'new').length;

  return (
    <div className="container-narrow page">
      <Link to="/dashboard" className="muted tiny">← Back to dashboard</Link>
      <div className="row between items-baseline mt-12">
        <h2 className="m-0">Inbox</h2>
        {newCount > 0 && <span className="muted tiny">{newCount} new</span>}
      </div>
      <p className="muted mt-6 mb-20">
        Claims on specific items and found reports from your personal code, together.
      </p>

      <div className="row wrap gap-6 mb-20" role="group" aria-label="Filter inbox">
        {['all', 'new', 'active', 'closed'].map((f) => (
          <button key={f} type="button" onClick={() => setFilter(f)}
            className={`btn toggle-btn is-filter${filter === f ? ' is-on' : ''}`}
            aria-pressed={filter === f}>
            {f}
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <div className="card center p-48">
          <p className="muted">
            {feed.length === 0
              ? 'Nothing yet. Share your code (Settings → Your code) and put QR labels on your items so finders can reach you.'
              : 'Nothing in this filter.'}
          </p>
        </div>
      ) : (
        <div className="stack">
          {shown.map((entry) => (
            <InboxCard key={`${entry.kind}-${entry.id}`} entry={entry} onChange={reload} />
          ))}
        </div>
      )}
    </div>
  );
}

function InboxCard({ entry, onChange }) {
  const [open, setOpen] = useState(false);
  const [photoUrls, setPhotoUrls] = useState([]);
  const [viewed, setViewed] = useState(!!entry.viewedAt);
  const [mode, setMode] = useState(null); // null | 'match' | 'reject'
  const [busy, setBusy] = useState(false);
  const [actionErr, setActionErr] = useState(null);

  const isReport = entry.kind === 'report';
  const unopened = isReport && !viewed && entry.status === 'SUBMITTED';
  const actionable = ACTIONABLE_STATUSES.includes(entry.status);

  async function toggleOpen() {
    const next = !open;
    setOpen(next);
    if (next) {
      if (photoUrls.length === 0 && (entry.photoKeys ?? []).length) {
        Promise.all(entry.photoKeys.map((k) => api.signPhotoView(k).then((r) => r.url)))
          .then(setPhotoUrls).catch(() => setPhotoUrls([]));
      }
      if (isReport && unopened) {
        try { await api.viewReport(entry.id); setViewed(true); onChange?.(); } catch { /* */ }
      }
    }
  }

  async function confirmClaim() {
    setBusy(true); setActionErr(null);
    try {
      await api.resolveClaim(entry.itemId, entry.id, 'CONFIRMED');
      onChange?.();
    } catch (e) { setActionErr(e.message); } finally { setBusy(false); }
  }
  async function rejectClaim() {
    setBusy(true); setActionErr(null);
    try {
      await api.resolveClaim(entry.itemId, entry.id, 'REJECTED');
      onChange?.();
    } catch (e) { setActionErr(e.message); } finally { setBusy(false); }
  }

  async function confirmReport(matchedItemIds) {
    setBusy(true); setActionErr(null);
    try {
      await api.confirmReport(entry.id, matchedItemIds);
      setMode(null);
      onChange?.();
    } catch (e) { setActionErr(e.message); } finally { setBusy(false); }
  }
  async function rejectReport(reason) {
    setBusy(true); setActionErr(null);
    try {
      await api.rejectReport(entry.id, reason);
      setMode(null);
      onChange?.();
    } catch (e) { setActionErr(e.message); } finally { setBusy(false); }
  }

  const tone = statusTone(entry.status);
  const toneClass = TONE_CLASSES[tone] ?? TONE_CLASSES.new;
  const title = isReport ? (entry.category || 'Found report') : (entry.itemName || 'Item claim');

  return (
    <div className={`card inbox-card${unopened ? ' is-unread' : ''}`}>
      <div
        className="card-header is-toggle"
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={toggleOpen}
        onKeyDown={(e) => {
          if (e.target !== e.currentTarget) return;
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleOpen(); }
        }}
      >
        <div>
          <div className="eyebrow eyebrow-row">
            <span className="kind-label">
              {isReport ? 'Report' : 'Claim'}
            </span>
            <span className="muted tiny">· {fmtRelativeTime(entry.createdAt)}</span>
          </div>
          <h4 className="inbox-title">
            {title}
            {isReport && entry.matchedItemNames?.length ? (
              <span className="muted fw-400"> → {entry.matchedItemNames.join(', ')}</span>
            ) : null}
          </h4>
        </div>
        <div className="row gap-8">
          <span className={`pill ${toneClass}`}>
            {STATUS_LABEL[entry.status] ?? entry.status}
          </span>
          <PaymentBadge paymentRequested={entry.paymentRequested} />
        </div>
      </div>

      {open && (
        <>
          <dl className="kv mt-4">
            <dt className="muted tiny">Finder</dt><dd>{entry.finderName}</dd>
            <dt className="muted tiny">Contact</dt><dd>{entry.finderContact}</dd>
            {entry.foundLocation && (<><dt className="muted tiny">Location</dt><dd>{entry.foundLocation}</dd></>)}
            {entry.description && (<><dt className="muted tiny">{isReport ? 'Description' : 'Message'}</dt><dd>{entry.description}</dd></>)}
            {entry.bountyTotalCents != null && (<><dt className="muted tiny">Bounty</dt><dd>${(entry.bountyTotalCents / 100).toFixed(0)}</dd></>)}
            {entry.rejectedReason && (<><dt className="muted tiny">Reason</dt><dd>{REASON_LABEL[entry.rejectedReason] ?? entry.rejectedReason}</dd></>)}
          </dl>

          {photoUrls.length > 0 && (
            <div className="photo-grid">
              {photoUrls.map((u, i) => (
                <a key={i} href={u} target="_blank" rel="noreferrer">
                  <img src={u} alt={`Finder photo ${i + 1}`} />
                </a>
              ))}
            </div>
          )}

          {actionErr && <div className="alert error mt-16">{actionErr}</div>}

          {/* ---- Claim actions ---- */}
          {!isReport && actionable && mode === null && (
            <div className="row mt-18">
              <button className="btn btn-primary" disabled={busy} onClick={confirmClaim}>
                {busy ? '…' : "Confirm — it's mine"}
              </button>
              <button className="btn btn-danger" disabled={busy} onClick={rejectClaim}>Reject</button>
              {entry.itemId && (
                <Link to={`/items/${entry.itemId}`} className="btn btn-ghost btn-sm">
                  Go to item
                </Link>
              )}
            </div>
          )}
          {!isReport && !actionable && entry.itemId && (
            <div className="row mt-18">
              <Link to={`/items/${entry.itemId}`} className="btn btn-ghost btn-sm">Go to item</Link>
            </div>
          )}

          {/* ---- Report actions ---- */}
          {isReport && actionable && mode === null && (
            <div className="row mt-18">
              <button className="btn btn-primary" disabled={busy} onClick={() => setMode('match')}>Confirm it's mine</button>
              <button className="btn btn-danger" disabled={busy} onClick={() => setMode('reject')}>Decline</button>
            </div>
          )}
          {isReport && mode === 'match' && (
            <MatchPicker reportId={entry.id} busy={busy}
              onCancel={() => setMode(null)}
              onConfirm={confirmReport} />
          )}
          {isReport && mode === 'reject' && (
            <RejectPicker busy={busy}
              onCancel={() => setMode(null)}
              onReject={rejectReport} />
          )}
        </>
      )}
    </div>
  );
}

/** Multi-item picker + on-the-spot creation for report identification. */
function MatchPicker({ reportId, busy, onCancel, onConfirm }) {
  const [items, setItems] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newBounty, setNewBounty] = useState('');
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.listItems().then((r) => setItems(r.items)).catch((e) => setErr(e.message));
  }, []);

  function toggle(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  async function createOnTheSpot() {
    if (!newName.trim()) return;
    setCreating(true); setErr(null);
    try {
      const cents = Math.round(parseFloat(newBounty || '0') * 100) || 0;
      const created = await api.createScaffoldItem(newName.trim(), cents, reportId);
      setItems((prev) => [created, ...(prev ?? [])]);
      setSelected((prev) => new Set(prev).add(created.itemId));
      setAdding(false); setNewName(''); setNewBounty('');
    } catch (e) { setErr(e.message); } finally { setCreating(false); }
  }

  return (
    <div className="subpanel">
      <div className="muted tiny subhead">
        Which of your items is this?
      </div>
      {err && <div className="alert error mb-12">{err}</div>}

      {items === null ? (
        <p className="muted tiny">Loading your items…</p>
      ) : (
        <div className="option-list is-scroll">
          {items.map((it) => (
            <label key={it.itemId} className={`option${selected.has(it.itemId) ? ' is-on' : ''}`}>
              <input type="checkbox" checked={selected.has(it.itemId)}
                onChange={() => toggle(it.itemId)} className="accent-gold" />
              <span className="flex-1">{it.name}</span>
              <span className="muted tiny">
                {[it.category, it.subcategory].filter(Boolean).join(' › ') || 'Uncategorized'}
              </span>
            </label>
          ))}
        </div>
      )}

      {/* On-the-spot creation */}
      {adding ? (
        <div className="dashed-box">
          <div className="muted tiny mb-8">Add an item you hadn't listed (no photo needed):</div>
          <div className="row wrap gap-8">
            <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)}
              placeholder="Item name" maxLength={120} className="spot-name" aria-label="Item name" />
            <div className="input-pair">
              <span className="muted tiny">$</span>
              <input type="number" min={0} value={newBounty} onChange={(e) => setNewBounty(e.target.value)}
                placeholder="bounty" className="spot-bounty" aria-label="Bounty in dollars" />
            </div>
            <button type="button" className="btn btn-primary" disabled={creating || !newName.trim()} onClick={createOnTheSpot}>
              {creating ? '…' : 'Add'}
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => setAdding(false)}>Cancel</button>
          </div>
        </div>
      ) : (
        <button type="button" className="btn btn-ghost mt-12 fs-13"
          onClick={() => setAdding(true)}>
          + This isn't in my inventory — add it
        </button>
      )}

      <div className="row mt-18">
        <button className="btn btn-primary" disabled={busy || selected.size === 0}
          onClick={() => onConfirm([...selected])}>
          {busy ? '…' : `Confirm match (${selected.size})`}
        </button>
        <button className="btn btn-ghost" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

/** Reject-with-reason picker (fixed reasons, shown to finder later). */
function RejectPicker({ busy, onCancel, onReject }) {
  const [reason, setReason] = useState('');
  return (
    <div className="subpanel">
      <div className="muted tiny subhead">
        Why are you declining?
      </div>
      <div className="option-list" role="radiogroup" aria-label="Reason">
        {REASON_OPTIONS.map((o) => (
          <label key={o.value} className={`option${reason === o.value ? ' is-on-ink' : ''}`}>
            <input type="radio" name="reject-reason" checked={reason === o.value}
              onChange={() => setReason(o.value)} />
            <span>{o.label}</span>
          </label>
        ))}
      </div>
      <p className="muted tiny mt-10">The finder will see this reason.</p>
      <div className="row mt-12">
        <button className="btn btn-danger" disabled={busy || !reason} onClick={() => onReject(reason)}>
          {busy ? '…' : 'Decline report'}
        </button>
        <button className="btn btn-ghost" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}
